import React, { useState, useEffect, FC, JSX } from "react";
import Accordion from "../ui/Accordion";
import Button from "../ui/Button";
import Loader from "../ui/Loader";
import ConfirmationDialog from "../ui/ConfirmationDialog";
import Badge from "../ui/Badge";
import { CompanyDetails, CompanyVerificationStatus } from "../../types/api";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

interface CompanyManagementTableProps {
  companies: CompanyDetails[] | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  onUpdateVerificationStatus: (
    companyId: string,
    data: { status: CompanyVerificationStatus; reason: string; userId: string }
  ) => Promise<boolean>;
  isAdminView?: boolean;
  pageTitle: string;
  pageDescription: string;
  showFilter?: boolean;
  showReviewGuidelines?: boolean;
  showVerificationHistory?: boolean;
  filterStatus?: CompanyVerificationStatus | "All";
  setFilterStatus?: React.Dispatch<
    React.SetStateAction<CompanyVerificationStatus | "All">
  >;
}

const CompanyManagementTable: FC<CompanyManagementTableProps> = ({
  companies,
  loading,
  error,
  onRefresh,
  onUpdateVerificationStatus,
  isAdminView = false,
  pageTitle,
  pageDescription,
  showFilter = false,
  showReviewGuidelines = false,
  showVerificationHistory = true,
  filterStatus,
  setFilterStatus,
}): JSX.Element => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
  const [companyToAction, setCompanyToAction] = useState<string | null>(null);
  const [actionType, setActionType] =
    useState<CompanyVerificationStatus | null>(null);
  const [actionReason, setActionReason] = useState<string>("");

  const getStatusColor = (status: CompanyVerificationStatus): string => {
    switch (status) {
      case "Verified":
        return "bg-green-100 text-green-800";
      case "Pending":
        return "bg-yellow-100 text-yellow-800";
      case "Rejected":
        return "bg-red-100 text-red-800";
      case "Blocked":
        return "bg-gray-100 text-gray-800";
      case "Incomplete":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleActionClick = (
    companyId: string,
    status: CompanyVerificationStatus
  ): void => {
    setCompanyToAction(companyId);
    setActionType(status);
    setIsConfirmOpen(true);
  };

  const handleConfirmAction = async (): Promise<void> => {
    if (!companyToAction || !actionType) return;

    setActionLoading(companyToAction);
    try {
      const success = await onUpdateVerificationStatus(companyToAction, {
        status: actionType,
        reason: actionReason,
        userId: user?.userId || "",
      });
      if (!success) {
        console.error(
          `Error ${actionType.toLowerCase()} company:`,
          companyToAction
        );
      }
    } catch (error: any) {
      console.error(`Error ${actionType.toLowerCase()} company:`, error);
    } finally {
      setActionLoading(null);
      setIsConfirmOpen(false);
      setCompanyToAction(null);
      setActionType(null);
      setActionReason("");
    }
  };

  const handleCancelAction = (): void => {
    setIsConfirmOpen(false);
    setCompanyToAction(null);
    setActionType(null);
    setActionReason("");
  };

  const filteredCompanies =
    companies?.filter(
      (company) =>
        !showFilter ||
        filterStatus === "All" ||
        company.verificationStatus === filterStatus
    ) || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{pageTitle}</h1>
        <p className="text-gray-600 mt-2">{pageDescription}</p>
      </div>

      {showReviewGuidelines && (
        <div className="bg-primary-lightGreen/20 border border-primary-lightGreen/20 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-primary-lightGreen"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-bold text-primary-green">
                Review Guidelines
              </h3>
              <div className="mt-2 text-sm text-primary-black">
                <ul className="list-disc list-inside space-y-1">
                  <li>Verify all company information is accurate</li>
                  <li>Check that required documents are uploaded and valid</li>
                  <li>
                    Ensure the company meets our registration requirements
                  </li>
                  <li>Contact the company if additional information is needed</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-start mb-4">
        <Button onClick={onRefresh} loading={loading} disabled={loading}>
          Refresh
        </Button>
      </div>

      {showFilter && setFilterStatus && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Filter Companies
            </h3>
            {/* Buttons for larger screens */}
            <div className="hidden sm:flex space-x-2">
              <button
                onClick={() => setFilterStatus("All")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterStatus === "All"
                    ? "bg-green-100 text-green-800 border border-green-300"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                All ({companies?.length || 0})
              </button>
              {(
                [
                  "Pending",
                  "Verified",
                  "Rejected",
                  "Blocked",
                  "Incomplete",
                ] as CompanyVerificationStatus[]
              ).map((status) => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filterStatus === status
                      ? getStatusColor(status) + " border border-green-300"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {status} (
                  {companies?.filter((c) => c.verificationStatus === status)
                    .length || 0}
                  )
                </button>
              ))}
            </div>

            {/* Dropdown for smaller screens */}
            <div className="sm:hidden">
              <select
                value={filterStatus}
                onChange={(e) =>
                  setFilterStatus(
                    e.target.value as CompanyVerificationStatus | "All"
                  )
                }
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-lightGreen focus:border-primary-lightGreen sm:text-sm rounded-md"
              >
                <option value="All">All ({companies?.length || 0})</option>
                {(
                  [
                    "Pending",
                    "Verified",
                    "Rejected",
                    "Blocked",
                    "Incomplete",
                  ] as CompanyVerificationStatus[]
                ).map((status) => (
                  <option
                    className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-lightGreen focus:border-primary-lightGreen sm:text-sm rounded-md"
                    key={status}
                    value={status}
                  >
                    {status} (
                    {companies?.filter((c) => c.verificationStatus === status)
                      .length || 0}
                    )
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader size="lg" />
        </div>
      ) : error ? (
        <div className="text-center py-12 text-red-600">Error: {error}</div>
      ) : filteredCompanies.length === 0 ? (
        <div className="text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 48 48"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No companies found
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Adjust your filters or check back later.
          </p>
        </div>
      ) : (
        <div className="grid gap-6">
          {filteredCompanies.map((company: CompanyDetails) => (
            <div
              key={company.companyId}
              className="bg-white rounded-lg shadow border border-gray-200 p-6"
            >
              <div className="sm:flex justify-between items-start">
                <div className="flex-1">
                  <div className="sm:flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {company.companyName}
                    </h3>
                    <div className="flex items-center space-x-2">
                      <span
                        className={`mt-4 sm:mt-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                          company.verificationStatus
                        )}`}
                      >
                        {company.verificationStatus}
                      </span>
                      <Button
                        className="mt-4 sm:mt-0 px-2.5 py-0.5"
                        variant='ghost'
                        size="sm"
                        onClick={() =>
                          navigate(`/${user?.role?.toLowerCase()}/companyProfile/${company.companyId}`)
                        }
                      >
                        View Profile
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-600">
                        <strong>Webpage:</strong> {company.webpageUrl || "N/A"}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Industry:</strong> {company.industry || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">
                        <strong>Employee Strength:</strong>{" "}
                        {company.empStrength || "N/A"}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Address:</strong> {company.address || "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-sm text-gray-600">
                      <strong>Submitted On:</strong>{" "}
                      {company.createdOn
                        ? new Date(company.createdOn).toLocaleDateString()
                        : "N/A"}
                    </p>
                  </div>

                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">
                      Documents
                    </h4>
                    <div className="flex space-x-4">
                      {company.attachments && company.attachments.length > 0 ? (
                        company.attachments.map((attachment) => (
                          <a
                            key={attachment.attachmentId}
                            href={attachment.fileUrl || "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-lightGreen hover:text-primary-black text-sm underline"
                          >
                            {attachment.originalName ||
                              `Document ${attachment.attachmentId}`}
                          </a>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500">
                          No documents uploaded.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {company.verificationStatus !== "Incomplete" && (
                  <div className="flex flex-col space-y-2 sm:ml-6">
                    {(company.verificationStatus === "Pending" ||
                      company.verificationStatus === "Rejected") && (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() =>
                          handleActionClick(company.companyId, "Verified")
                        }
                        loading={actionLoading === company.companyId}
                        disabled={actionLoading === company.companyId}
                      >
                        Approve
                      </Button>
                    )}
                    {company.verificationStatus === "Blocked" && isAdminView && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() =>
                          handleActionClick(company.companyId, "Verified")
                        }
                        loading={actionLoading === company.companyId}
                        disabled={actionLoading === company.companyId}
                      >
                        Unblock
                      </Button>
                    )}
                    {company.verificationStatus !== "Rejected" &&
                      (company.verificationStatus !== "Blocked" ||
                        isAdminView) && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() =>
                            handleActionClick(company.companyId, "Rejected")
                          }
                          loading={actionLoading === company.companyId}
                          disabled={actionLoading === company.companyId}
                        >
                          Reject
                        </Button>
                      )}
                    {company.verificationStatus !== "Blocked" && (
                      <Button
                        variant="warning"
                        size="sm"
                        onClick={() =>
                          handleActionClick(company.companyId, "Blocked")
                        }
                        loading={actionLoading === company.companyId}
                        disabled={actionLoading === company.companyId}
                      >
                        Block
                      </Button>
                    )}
                  </div>
                )}
              </div>
              {showVerificationHistory &&
                company.verificationHistory &&
                company.verificationHistory.length > 0 && (
                  <div className="mt-4">
                    <Accordion title="Verification History">
                      <div className="space-y-2">
                        {company.verificationHistory.map((action, index) => (
                          <div
                            key={index}
                            className={`p-3 rounded-md ${getStatusColor(
                              action.verificationStatus
                            )}`}
                          >
                            <p className="text-l font-medium">
                              Status: {action.verificationStatus}
                            </p>
                            <p className="text-sm font-medium">
                              Reason: {action.reason || "N/A"}
                            </p>
                            <p className="text-sm">
                              by: {action.updatedByName || "N/A"}
                            </p>
                            <p className="text-sm">
                              on:{" "}
                              {action.updatedOn
                                ? new Date(
                                    action.updatedOn
                                  ).toLocaleDateString()
                                : "N/A"}
                            </p>
                          </div>
                        ))}
                      </div>
                    </Accordion>
                  </div>
                )}
            </div>
          ))}
        </div>
      )}
      <ConfirmationDialog
        isOpen={isConfirmOpen}
        onClose={handleCancelAction}
        onConfirm={handleConfirmAction}
        title={`${actionType} Company`}
        message={`Please provide a reason for ${actionType?.toLowerCase()} this company:`}
        confirmText={
          actionType === "Blocked"
            ? "Block"
            : actionType === "Rejected"
            ? "Reject"
            : "Confirm"
        }
        variant={
          actionType === "Rejected" || actionType === "Blocked"
            ? "danger"
            : "info"
        }
        isLoading={actionLoading !== null}
      >
        <textarea
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-primary-lightGreen focus:border-primary-lightGreen"
          rows={4}
          placeholder={`Reason for ${actionType?.toLowerCase()}...`}
          value={actionReason}
          onChange={(e) => setActionReason(e.target.value)}
        ></textarea>
      </ConfirmationDialog>
    </div>
  );
};

export default CompanyManagementTable;
